/**
 * The week's move plumbing: one live region, and one dispatch that the pointer, the keyboard and
 * the ⋯ menu all go through.
 *
 * It decides nothing. `runMoveCommand` gates and applies; `dayMove.ts` words the result; this hook
 * puts the two together and holds the sentence for the region to read. That is deliberately thin —
 * what it buys is that the three entry points cannot drift, because there is only one of them.
 *
 * ── Why the announcement is produced HERE and not in the card ────────────────────────────────
 * The outcome comes back from the store, which is outside React, and the words need `t`, which is
 * inside it. A card producing its own sentence would need `t`, the weekday table and the refusal
 * vocabulary — three things a component may not hold — and seven cards would hold seven copies.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import { runMoveCommand } from '~/entities/plan-draft';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';

import { dayMoveAnnouncement } from '../model/dayMove';

/** What the sentence needs and the command does not: which session moved, and from where. */
export interface DayMoveContext {
  readonly sessionName: string;
  /** The card's own STOP index — 0-based, so weekday 1 is `0`. See `SessionMoveProps.moveDay`. */
  readonly from: number;
}

export interface WeekMoveState {
  /** Rendered into the canvas's always-mounted live region. */
  readonly announcement: string;
  /** Say something without moving anything — the keyboard path's per-arrow preview, and a cancel. */
  readonly announce: (message: string) => void;
  /**
   * Which STOP a KEYBOARD lift is currently pointed at — 0-based, so weekday 1 is `0` — or `null`
   * when nothing is lifted.
   *
   * It lives on the pane rather than inside the lifted card because the column that has to light up
   * is a SIBLING of that card — a lifted Monday pointing at Thursday can style neither Thursday's
   * slot nor its header from inside itself. Exactly one lift exists at a time on a week, so one
   * value covers it.
   *
   * A sighted keyboard user would otherwise get the announcement and nothing on screen, which is the
   * "announced but invisible" half of the same gap the pointer path's indicator closes.
   */
  readonly pendingTarget: number | null;
  readonly setPendingTarget: (index: number | null) => void;
  /**
   * Send a day to a weekday, and say what happened. The ONE write behind all three paths.
   *
   * `context` is what the sentence needs and the command does not: which session moved and where it
   * stood. Both are already on the card's view model, so nothing is looked up twice.
   */
  readonly moveDay: (
    daySlotToken: string,
    targetWeekday: number,
    context: DayMoveContext,
  ) => void;
  /**
   * The day a completed move owes focus to, or `null`.
   *
   * A committed move re-renders the card into ANOTHER COLUMN: the instance the trainer was standing
   * on unmounts and focus falls to `<body>`, so the next Tab restarts at the top of the document
   * with nothing said about where the workout went. Both non-pointer paths end there — the
   * keyboard lift's Space, and the menu's weekday entry, whose own trigger Base UI would restore to
   * is inside the card that has just gone.
   *
   * It is the same signal the dock already uses to hand a card its focus back, so the card needs no
   * second mechanism: the canvas merges the two and the column hands `shouldTakeFocus` down. The
   * tape's twin carries the identical pair — see `useTapeMove`.
   */
  readonly focusToken: string | null;
  /** Clear the signal once the card has taken it, so a later re-mount cannot steal focus. */
  readonly focusTaken: () => void;
}

export function useWeekMove(weekdayLabels: readonly string[]): WeekMoveState {
  const { t } = useTranslation();
  const { message, announce } = useAnnouncer();
  const [pendingTarget, setPendingTarget] = useState<number | null>(null);
  const [focusToken, setFocusToken] = useState<string | null>(null);

  const moveDay = (
    daySlotToken: string,
    targetWeekday: number,
    context: DayMoveContext,
  ): void => {
    setPendingTarget(null);
    setFocusToken(daySlotToken);
    const outcome = runMoveCommand({ kind: 'moveDayToWeekday', daySlotToken, targetWeekday });
    announce(
      dayMoveAnnouncement(t, outcome, {
        sessionName: context.sessionName,
        weekdayLabels,
        currentWeekday: context.from + 1,
      }),
    );
  };

  // The latest `moveDay`, so the monitor below can be bound ONCE. Rebinding it every render would
  // tear the monitor down mid-gesture, and a monitor unbound between `onDragStart` and `onDrop`
  // never sees the drop at all. Written in an EFFECT rather than during render — `react-hooks/refs`
  // — which is safe because the only reader is a drop callback, long after effects have run.
  const latest = useRef(moveDay);
  useEffect(() => {
    latest.current = moveDay;
  });

  /**
   * The POINTER path's dispatch, in one place for the whole canvas.
   *
   * A monitor rather than seven per-slot handlers, and the reason is the release that lands on
   * NOTHING: view ADR §11 says "a drop outside any target cancels", and only a monitor sees that
   * case — a target's own `onDrop` never fires for it. So both endings are decided together here,
   * and a cancelled pointer drag takes exactly the path a cancelled keyboard lift does: the pending
   * highlight clears, nothing is dispatched, and the tree is untouched.
   *
   * Everything the sentence needs travels on the drag PAYLOAD. Reading the title back out of the
   * tree at drop time would name whatever now stands on that weekday, which after a swap is the
   * other session.
   */
  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) => source.data.kind === 'session',
        onDrop: ({ source, location }) => {
          setPendingTarget(null);
          const weekday = location.current.dropTargets[0]?.data.weekday;
          const daySlotToken = source.data.daySlotToken;
          if (typeof weekday !== 'number' || typeof daySlotToken !== 'string') return;
          latest.current(daySlotToken, weekday, {
            sessionName: typeof source.data.sessionName === 'string' ? source.data.sessionName : '',
            from: typeof source.data.from === 'number' ? source.data.from : 0,
          });
        },
      }),
    [],
  );

  return {
    announcement: message,
    announce,
    pendingTarget,
    setPendingTarget,
    moveDay,
    focusToken,
    focusTaken: () => {
      setFocusToken(null);
    },
  };
}
